import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import type { PaginatedResponse } from "../../../api/src/lib";
import type { ConsoleReleaseNote } from "../../../api/src/release-notes/release-notes.service";
import ReleaseNoteBody from "./ReleaseNoteBody";
import "./ReleaseLog.scss";

// The page asks for 10 release notes in each request (R43); the route
// otherwise answers with 50.
export const RELEASE_NOTES_PAGE_SIZE = 10;

export const LOAD_ERROR_TEXT = "Release notes did not load. Try again later.";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * `2026-10-09` → `October 9, 2026` (R13). Built from the three numbers of the
 * string, never through `Date`: `new Date("2026-10-09")` is midnight UTC, which
 * is the day before in a time zone west of UTC.
 */
export function formatReleaseDate(releaseDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(releaseDate);
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  if (!match || !month) {
    return releaseDate;
  }
  return `${month} ${Number(match[3])}, ${match[1]}`;
}

interface ReleaseDateGroup {
  releaseDate: string;
  notes: ConsoleReleaseNote[];
}

/**
 * One group for each run of adjacent release notes with the same
 * `releaseDate`, in the order of the answer (R12, R38, R39). The page does not
 * sort: the route orders by release date first, so each date is one run.
 */
export function groupByReleaseDate(
  notes: ConsoleReleaseNote[],
): ReleaseDateGroup[] {
  const groups: ReleaseDateGroup[] = [];
  for (const note of notes) {
    const last = groups[groups.length - 1];
    if (last && last.releaseDate === note.releaseDate) {
      last.notes.push(note);
    } else {
      groups.push({ releaseDate: note.releaseDate, notes: [note] });
    }
  }
  return groups;
}

export const SHOW_OLDER_TEXT = "Show older release notes";

export default function ReleaseLog() {
  const { getAccessTokenSilently } = useAuth0();
  const [notes, setNotes] = useState<ConsoleReleaseNote[]>([]);
  // From the last answer with status 200; 0 before the first one, so the page
  // shows no button until then (R18, R35).
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState(false);
  const mounted = useRef(true);
  // A second click while a request of the button runs would send the same
  // offset again and show its release notes twice.
  const loadingOlderRef = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * One `GET /release-notes` with `limit=10` (R43) at `offset`. Any status but
   * 200, a network error or a body that is not JSON is a failed request: the
   * page shows the alert (R21) and keeps the release notes and the total it
   * has (R31, R34), so the next click sends the same `offset` again.
   */
  const loadPage = useCallback(
    async (offset: number) => {
      setError(false);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT}/release-notes?limit=${RELEASE_NOTES_PAGE_SIZE}&offset=${offset}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.status !== 200) {
          throw new Error(`GET /release-notes returned ${response.status}`);
        }
        const answer: PaginatedResponse<ConsoleReleaseNote> =
          await response.json();
        if (mounted.current) {
          // Appended in the order of the answer (R19); the route sorts by
          // release date first, so a shown date keeps its one heading (R40).
          setNotes((shown) =>
            offset === 0 ? answer.data : [...shown, ...answer.data],
          );
          setTotalCount(answer.pagination.totalCount);
        }
      } catch {
        if (mounted.current) {
          setError(true);
        }
      }
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    setLoading(true);
    loadPage(0).finally(() => {
      if (mounted.current) {
        setLoading(false);
      }
    });
  }, [loadPage]);

  async function showOlder() {
    if (loadingOlderRef.current) {
      return;
    }
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    await loadPage(notes.length);
    loadingOlderRef.current = false;
    if (mounted.current) {
      setLoadingOlder(false);
    }
  }

  return (
    <div className="release-log">
      <h1 className="visually-hidden">Release Notes</h1>
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {notes.length === 0 && !error && (
            <p className="text-muted">No release notes yet.</p>
          )}
          {groupByReleaseDate(notes).map((group, index) => (
            <section key={`${group.releaseDate}-${index}`}>
              <h2>{formatReleaseDate(group.releaseDate)}</h2>
              {group.notes.map((note) => (
                <article key={note.id}>
                  <h3>{note.title}</h3>
                  {/* ReleaseLog.scss colors the label by data-group (R48). */}
                  <span className="badge" data-group={note.group}>
                    {note.group}
                  </span>
                  <ReleaseNoteBody body={note.body} />
                </article>
              ))}
            </section>
          ))}
          {error && (
            <div className="alert alert-danger" role="alert">
              {LOAD_ERROR_TEXT}
            </div>
          )}
          {notes.length < totalCount && (
            <Button
              variant="outline-primary"
              onClick={showOlder}
              disabled={loadingOlder}
            >
              {SHOW_OLDER_TEXT}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
